//
import axios from 'axios';
// config
import { HOST_API } from 'src/config-global';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({ baseURL: HOST_API });

axiosInstance.interceptors.request.use((config) => {
  const token = sessionStorage.getItem('patientToken');
  if (token) {
    config.headers['x-patient-token'] = token;
    
    // Clean token out of URL to present a proper structure like /current 
    if (config.url && config.url.includes(token)) {
      config.url = config.url.replace(token, 'current');
    }
    // Clean token out of body
    if (config.data && typeof config.data === 'object' && config.data.patientId === token) {
      config.data.patientId = 'current';
    }
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (res) => res,
  (error) => {
    const data = error?.response?.data;

    // Preserve existing behavior for string errors
    if (typeof data === 'string') {
      return Promise.reject(data);
    }

    // Normalize common backend shapes to { error: { message } }
    if (data && typeof data === 'object') {
      const message = data?.error?.message || data?.message;
      if (message) {
        return Promise.reject({ ...data, error: { message } });
      }
      return Promise.reject(data);
    }

    return Promise.reject('Something went wrong');
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  chat: '/api/chat',
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  dashboard: {
    overview: '/api/v1/dashboard/overview',
  },
  patients: {
    create: '/api/v1/patients',
    pending: '/api/v1/claims/pending',
    myApprovals: '/api/v1/claims/my-approvals',
    documents: (patientId) => `/api/v1/claims/${patientId}/documents`,
    details: (id) => `/api/v1/patients/${id}`,
    policyCoverage: (id) => `/api/v1/claims/${id}/policy-coverage`,
    claimType: (id) => `/api/v1/patients/${id}/claim-type`,
    approve: (id) => `/api/v1/claims/${id}/approve`,
    reject: (id) => `/api/v1/claims/${id}/reject`,
    faceLogin: '/api/v1/patients/face-login',
    sessionToken: (id) => `/api/v1/patients/${id}/session-token`,
  },
  claims: {
    list: '/api/v1/claims',
    createFromPatient: (patientId) => `/api/v1/claims/from-patient/${patientId}`,
    receiptsEligible: '/api/v1/claims/receipts/eligible',
    receipts: '/api/v1/claims/receipts',
    sessionToken: (id) => `/api/v1/claims/${id}/session-token`,
  },
  finance: {
    details: (patientId) => `/api/v1/finance/${patientId}`,
    process: '/api/v1/finance/process',
  },
  auth: {
    me: '/api/v1/auth/me',
    login: '/api/v1/auth/login',
    register: '/api/v1/auth/register',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  scheduler: {
    list: '/schedulers',
    filterList: (filter) => `/schedulers?filter=${filter}`,
    details: (id) => `/schedulers/${id}`,
  },
  designation: {
    list: '/designations',
    filterList: (filter) => `/designations?filter=${filter}`,
    details: (id) => `/designations/${id}`,
  },
};
